import { useEffect, useState } from "react";
import { api, type Earnings } from "./api";
import { earningsCopy } from "./earningsCopy";

// Where a producer's money came from: the backing share still accruing on
// live contests, then each settled contest's backing share and winner's cut
// as separate lines, then lifetime totals. Every figure is the server's.

const sectionLabel = "text-[11px] uppercase tracking-[0.18em] text-chrome-dim";
const amountClass = "bf-tabular font-bold text-money";

function ContestTitle({ slug, title }: { slug: string; title: string }) {
  const label = title || earningsCopy.pastContest;
  if (!slug) return <span className="text-[13px] font-bold text-chrome">{label}</span>;
  return (
    <a href={`/contest/${encodeURIComponent(slug)}`} className="bf-focusable text-[13px] font-bold text-gold hover:text-gold-light">
      {label}
    </a>
  );
}

export default function EarningsPanel() {
  const [data, setData] = useState<Earnings | null>(null);
  const [failed, setFailed] = useState(false);

  const load = async () => {
    const res = await api.earnings();
    if (res.ok) {
      setData(res.data);
      setFailed(false);
    } else setFailed(true);
  };

  useEffect(() => {
    void load();
  }, []);

  if (failed && !data) {
    return (
      <section data-testid="earnings-panel" className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
        <p className={sectionLabel}>{earningsCopy.heading}</p>
        <p data-testid="earnings-error" className="mt-3 text-sm text-chrome-dim">
          {earningsCopy.error}
        </p>
        <button
          type="button"
          onClick={() => void load()}
          className="bf-focusable mt-3 text-[11px] font-bold uppercase tracking-[0.14em] text-money"
        >
          {earningsCopy.reload}
        </button>
      </section>
    );
  }
  if (!data) return null;

  const empty = data.live.length === 0 && data.contests.length === 0;

  return (
    <section data-testid="earnings-panel" className="rounded-2xl border border-ground-edge bg-ground-raised p-5">
      <p className={sectionLabel}>{earningsCopy.heading}</p>

      {empty && (
        <p data-testid="earnings-empty" className="mt-3 text-[13px] leading-relaxed text-chrome-dim">
          {earningsCopy.empty}
        </p>
      )}

      {data.live.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{earningsCopy.live}</p>
          <ul className="mt-2 flex flex-col gap-2">
            {data.live.map((l, i) => (
              <li
                key={`${l.contest_slug}-${i}`}
                data-testid="earnings-live-row"
                className="flex flex-wrap items-baseline justify-between gap-2 rounded-lg border border-ground-edge bg-ground px-3 py-2.5"
              >
                <ContestTitle slug={l.contest_slug} title={l.contest_title} />
                <span className={`${amountClass} text-[13px]`}>{earningsCopy.liveRow(l.earned_display, l.backers)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[11px] leading-relaxed text-chrome-dim">{earningsCopy.liveNote}</p>
        </div>
      )}

      {data.contests.length > 0 && (
        <div className="mt-5">
          <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{earningsCopy.byContest}</p>
          <ul className="mt-2 flex flex-col gap-2">
            {data.contests.map((c, i) => (
              <li
                key={`${c.contest_slug}-${c.paid_at}-${i}`}
                data-testid="earnings-contest"
                className="rounded-lg border border-ground-edge bg-ground px-3 py-2.5"
              >
                <ContestTitle slug={c.contest_slug} title={c.contest_title} />
                {c.backing_share_cents > 0 && (
                  <p data-testid="earnings-backing" className="mt-1.5 flex justify-between gap-3 text-[12px] text-chrome-dim">
                    <span>{earningsCopy.backingShare}</span>
                    <span className={amountClass}>{c.backing_share_display}</span>
                  </p>
                )}
                {c.winner_cut_cents > 0 && (
                  <p data-testid="earnings-cut" className="mt-1 flex justify-between gap-3 text-[12px] text-chrome-dim">
                    <span>{earningsCopy.winnerCut}</span>
                    <span className={amountClass}>{c.winner_cut_display}</span>
                  </p>
                )}
              </li>
            ))}
          </ul>
          <div data-testid="earnings-lifetime" className="mt-4 rounded-lg border border-ground-edge bg-ground px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-[0.18em] text-chrome-dim">{earningsCopy.lifetime}</p>
            {(
              [
                [earningsCopy.backingShare, data.totals.backing_share_display],
                [earningsCopy.winnerCut, data.totals.winner_cut_display],
                [earningsCopy.total, data.totals.total_display],
              ] as const
            ).map(([label, value]) => (
              <p key={label} className="mt-1.5 flex justify-between gap-3 text-[12px] text-chrome-dim">
                <span>{label}</span>
                <span className={amountClass}>{value}</span>
              </p>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
